import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TourTooltipComponent } from '../tour-tooltip/tour-tooltip.component';
import { TourStep } from '../../models/tour-step.model';
import { TourService } from '../../services/tour.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-tour-overlay',
  imports: [CommonModule, TourTooltipComponent],
  templateUrl: './tour-overlay.component.html',
  styleUrl: './tour-overlay.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export class TourOverlayComponent implements OnInit, OnDestroy {
  step: TourStep | null = null;

  highlightStyle: Record<string, string> = {
    display: 'none',
  };

  tooltipStyle: Record<string, string> = {};
  isTransitioning = false;
  private positionRunId = 0;
  total = 0;
  currentIndex = 0;
  isPositioned = false;
  isTourActive = false;
  get isRtl(): boolean {
    return document.dir === 'rtl';
  }

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly tour = inject(TourService);
  private readonly renderer = inject(Renderer2);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.registerInteractionGuards();

    this.tour.active$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((active) => {
        this.isTourActive = active;

        if (active) {
          this.renderer.addClass(document.body, 'tour-freeze');

          setTimeout(() => {
            this.focusTourControls();
          });
        } else {
          this.unlockPage();
        }

        this.cdr.detectChanges();
      });

    /*
     * در کد قبلی این subscription دوبار نوشته شده بود.
     */
    this.tour.total$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((total) => {
        this.total = total;
      });

    this.tour.currentIndex$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((index) => {
        this.currentIndex = index >= 0 ? index + 1 : 0;
      });

    this.tour.step$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((step) => {
        const runId = ++this.positionRunId;

        this.step = step;
        this.isPositioned = false;

        /*
         * در فاصله بین دو مرحله فقط Backdrop باقی می‌ماند.
         * Highlight و Tooltip قبلی مخفی می‌شوند.
         */
        this.highlightStyle = {
          display: 'none',
        };

        this.removeActiveTarget();

        if (!step) {
          this.isTransitioning = false;
          this.cdr.detectChanges();
          return;
        }

        this.isTransitioning = true;
        this.cdr.detectChanges();

        void this.positionStep(step, runId);
      });
  }
  private getScrollableParent(el: HTMLElement): HTMLElement | Window {
    let parent = el.parentElement;

    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;

      const canScroll =
        (overflowY === 'auto' || overflowY === 'scroll') &&
        parent.scrollHeight > parent.clientHeight;

      if (canScroll) {
        return parent;
      }

      parent = parent.parentElement;
    }

    return window;
  }
  private async positionStep(step: TourStep, runId: number): Promise<void> {
    /*
     * منتظر می‌مانیم Target توسط Angular رندر شود.
     */
    const el = await this.waitForTarget(step.targetId, runId);

    if (!el || !this.isCurrentPositionRun(runId)) {
      return;
    }

    /*
     * ابتدا Parentها و منوهای بسته باز می‌شوند.
     */
    this.expandParents(el);

    /*
     * منتظر پایان باز شدن Submenu و تغییرات Layout می‌مانیم.
     */
    await this.waitForElementStable(el, runId);

    if (!this.isCurrentPositionRun(runId)) {
      return;
    }

    const isMobile = window.innerWidth < 992;
    const scrollParent = this.getScrollableParent(el);

    this.scrollTargetIntoView(el, isMobile, scrollParent);

    /*
     * به‌جای زمان ثابت، پایان واقعی Scroll بررسی می‌شود.
     */
    await this.waitForScrollSettled(scrollParent, el, runId);

    if (!this.isCurrentPositionRun(runId)) {
      return;
    }

    /*
     * ممکن است بعد از Scroll هنوز Layout کمی جابه‌جا شود.
     */
    await this.waitForElementStable(el, runId);

    if (!this.isCurrentPositionRun(runId) || !el.isConnected) {
      return;
    }

    this.setTooltipPosition(el);

    this.isTransitioning = false;
    this.cdr.detectChanges();

    requestAnimationFrame(() => {
      this.focusTourControls();
    });
  }
  private scrollTargetIntoView(
    el: HTMLElement,
    isMobile: boolean,
    scrollParent: HTMLElement | Window
  ): void {
    if (scrollParent === window) {
      el.scrollIntoView({
        behavior: isMobile ? 'auto' : 'smooth',
        block: 'center',
        inline: 'nearest',
      });

      return;
    }

    const parent = scrollParent as HTMLElement;
    const parentRect = parent.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    const verticalPosition = isMobile ? 0.35 : 0.5;

    const offsetTop =
      elRect.top -
      parentRect.top +
      parent.scrollTop -
      parent.clientHeight * verticalPosition +
      elRect.height / 2;

    parent.scrollTo({
      top: Math.max(0, offsetTop),
      behavior: isMobile ? 'auto' : 'smooth',
    });
  }
  private waitForTarget(
    targetId: string,
    runId: number,
    timeout = 2000
  ): Promise<HTMLElement | null> {
    return new Promise((resolve) => {
      const startedAt = performance.now();

      const check = () => {
        if (!this.isCurrentPositionRun(runId)) {
          resolve(null);
          return;
        }

        const el = document.getElementById(targetId);

        if (el) {
          resolve(el);
          return;
        }

        if (performance.now() - startedAt >= timeout) {
          resolve(null);
          return;
        }

        requestAnimationFrame(check);
      };

      check();
    });
  }
  private waitForElementStable(
    el: HTMLElement,
    runId: number,
    timeout = 2000
  ): Promise<void> {
    return new Promise((resolve) => {
      const startedAt = performance.now();

      let previousRect = el.getBoundingClientRect();
      let stableFrames = 0;

      const check = () => {
        if (!this.isCurrentPositionRun(runId) || !el.isConnected) {
          resolve();
          return;
        }

        const currentRect = el.getBoundingClientRect();

        const isVisible = currentRect.width > 0 && currentRect.height > 0;

        const isStable =
          Math.abs(currentRect.top - previousRect.top) < 0.5 &&
          Math.abs(currentRect.left - previousRect.left) < 0.5 &&
          Math.abs(currentRect.width - previousRect.width) < 0.5 &&
          Math.abs(currentRect.height - previousRect.height) < 0.5;

        if (isVisible && isStable) {
          stableFrames++;
        } else {
          stableFrames = 0;
        }

        previousRect = currentRect;

        const timedOut = performance.now() - startedAt >= timeout;

        if (stableFrames >= 4 || timedOut) {
          resolve();
          return;
        }

        requestAnimationFrame(check);
      };

      requestAnimationFrame(check);
    });
  }
  private waitForScrollSettled(
    scrollParent: HTMLElement | Window,
    el: HTMLElement,
    runId: number,
    timeout = 3500
  ): Promise<void> {
    return new Promise((resolve) => {
      const startedAt = performance.now();

      let previousScrollPosition = this.getScrollPosition(scrollParent);

      let previousElementTop = el.getBoundingClientRect().top;

      let stableFrames = 0;

      const check = () => {
        if (!this.isCurrentPositionRun(runId) || !el.isConnected) {
          resolve();
          return;
        }

        const currentScrollPosition = this.getScrollPosition(scrollParent);

        const currentElementTop = el.getBoundingClientRect().top;

        const scrollIsStable =
          Math.abs(currentScrollPosition - previousScrollPosition) < 0.5;

        const elementIsStable =
          Math.abs(currentElementTop - previousElementTop) < 0.5;

        if (scrollIsStable && elementIsStable) {
          stableFrames++;
        } else {
          stableFrames = 0;
        }

        previousScrollPosition = currentScrollPosition;
        previousElementTop = currentElementTop;

        const timedOut = performance.now() - startedAt >= timeout;

        /*
         * پنج فریم ثابت یعنی Smooth Scroll تمام شده است.
         */
        if (stableFrames >= 5 || timedOut) {
          resolve();
          return;
        }

        requestAnimationFrame(check);
      };

      requestAnimationFrame(check);
    });
  }
  private expandParents(el: HTMLElement) {
    document
      .querySelectorAll('li.expanded')
      .forEach((li) => li.classList.remove('expanded'));

    let parentLi = el.closest('li');
    while (parentLi) {
      const subUl = parentLi.querySelector('ul');
      if (subUl) {
        parentLi.classList.add('expanded');
      }
      parentLi = parentLi.parentElement?.closest('li') || null;
    }
  }
  private setTooltipPosition(el: HTMLElement) {
    document
      .querySelectorAll('.tour-active')
      .forEach((e) => e.classList.remove('tour-active'));
    el.classList.add('tour-active');

    const rect = el.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const isMobile = viewportWidth < 992;

    this.highlightStyle = {
      top: `${rect.top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
      height: `${rect.height}px`,
      position: 'fixed',
      'box-shadow': '0 0 0 9999px rgba(0, 0, 0, 0.7)',
      'border-radius': '12px',
      'z-index': '2147483001',
      'pointer-events': 'none',
    };

    const finalStyle: Record<string, string> = {
      position: 'fixed',
      'z-index': '2147483003',
    };

    if (!isMobile && this.step?.position === 'bottom') {
      const width = Math.min(320, viewportWidth - 32);
      const height = 240;
      finalStyle['width'] = `${width}px`;
      finalStyle['left'] = `${Math.max(16, Math.min(this.isRtl ? rect.right - width : rect.left, viewportWidth - width - 16))}px`;
      if (viewportHeight - rect.bottom >= height + 16) {
        finalStyle['top'] = `${rect.bottom + 12}px`;
      } else {
        finalStyle['bottom'] = `${Math.max(16, viewportHeight - rect.top + 12)}px`;
      }
    } else if (isMobile) {
      finalStyle['width'] = 'calc(100% - 32px)';
      finalStyle['left'] = '16px';

      const spaceAbove = rect.top;
      const spaceBelow = viewportHeight - rect.bottom;
      const tooltipHeight = 180;

      if (spaceBelow > tooltipHeight + 20) {
        finalStyle['top'] = `${rect.bottom + 15}px`;
        finalStyle['bottom'] = 'auto';
      } else if (spaceAbove > tooltipHeight + 20) {
        finalStyle['bottom'] = `${viewportHeight - rect.top + 15}px`;
        finalStyle['top'] = 'auto';
      } else {
        finalStyle['bottom'] = 'env(safe-area-inset-bottom, 20px)';
        finalStyle['top'] = 'auto';
        finalStyle['left'] = '16px';
        finalStyle['right'] = '16px';
        finalStyle['width'] = 'auto';
      }
    } else {
      const gap = 20;
      const margin = 16;

      const tooltipWidth = Math.min(320, viewportWidth - margin * 2);
      finalStyle['width'] = `${tooltipWidth}px`;

      if (viewportHeight - rect.top < 220) {
        finalStyle['bottom'] = `${viewportHeight - rect.bottom}px`;
        finalStyle['top'] = 'auto';
      } else {
        finalStyle['top'] = `${rect.top}px`;
        finalStyle['bottom'] = 'auto';
      }

      const isRtl = document.dir === 'rtl';

      let desiredLeft: number;

      if (isRtl) {
        if (rect.left > tooltipWidth + gap) {
          desiredLeft = rect.left - tooltipWidth - gap;
        } else {
          desiredLeft = rect.right + gap;
        }
      } else {
        if (viewportWidth - rect.right > tooltipWidth + gap) {
          desiredLeft = rect.right + gap;
        } else {
          desiredLeft = rect.left - tooltipWidth - gap;
        }
      }
      const minLeft = margin;
      const maxLeft = viewportWidth - tooltipWidth - margin;

      finalStyle['left'] = `${Math.max(
        minLeft,
        Math.min(desiredLeft, maxLeft)
      )}px`;
      finalStyle['right'] = 'auto';
    }

    this.tooltipStyle = finalStyle;
    this.isPositioned = true;
    this.cdr.detectChanges();

    if (this.step?.position === 'bottom') {
      const tooltip = document.querySelector<HTMLElement>('.tour-tooltip');
      if (tooltip) {
        const height = tooltip.getBoundingClientRect().height;
        const below = rect.bottom + 12;
        const above = rect.top - height - 12;
        const top = below + height <= viewportHeight - 16
          ? below
          : above >= 16 ? above : Math.max(16, viewportHeight - height - 16);
        this.tooltipStyle = { ...finalStyle, top: `${top}px`, bottom: 'auto' };
        this.cdr.detectChanges();
      }
    }
  }
  next(): void {
    if (!this.isTourActive || this.isTransitioning) {
      return;
    }

    this.isTransitioning = true;
    this.tour.next();
  }
  previous(): void {
    if (!this.isTourActive || this.isTransitioning || this.currentIndex <= 1) {
      return;
    }
    this.isTransitioning = true;
    this.tour.previous();
  }

  @HostListener('window:resize')
  onResize(): void {
    const target = this.step && document.getElementById(this.step.targetId);
    if (target && this.isPositioned && !this.isTransitioning) {
      this.setTooltipPosition(target);
    }
  }
  close(): void {
    this.positionRunId++;
    this.isTransitioning = false;

    this.removeActiveTarget();
    this.tour.end();
  }
  blockInteraction(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
  }
  ngOnDestroy(): void {
    this.positionRunId++;
    this.isTransitioning = false;

    this.unregisterInteractionGuards();
    this.unlockPage();

    if (this.isTourActive) {
      this.tour.end();
    }
  }
  private unlockPage(): void {
    this.isTourActive = false;
    this.isPositioned = false;
    this.isTransitioning = false;
    this.step = null;

    this.highlightStyle = {
      display: 'none',
    };

    this.tooltipStyle = {};

    this.renderer.removeClass(document.body, 'tour-freeze');

    this.removeActiveTarget();
  }
  private registerInteractionGuards(): void {
    document.addEventListener('keydown', this.handleDocumentKeydown, true);

    document.addEventListener('focusin', this.handleDocumentFocus, true);
  }
  private unregisterInteractionGuards(): void {
    document.removeEventListener('keydown', this.handleDocumentKeydown, true);

    document.removeEventListener('focusin', this.handleDocumentFocus, true);
  }
  private readonly handleDocumentKeydown = (event: KeyboardEvent): void => {
    if (!this.isTourActive) {
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }

    const tooltip = document.querySelector<HTMLElement>('.tour-tooltip');
    const target = event.target as Node | null;

    /*
     * تمامی تعاملات کیبورد خارج از Tooltip مسدود می‌شوند.
     */
    if (!tooltip || !target || !tooltip.contains(target)) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.focusTourControls();
      return;
    }
    if (event.key === 'Tab') {
      this.trapTooltipFocus(event, tooltip);
    }
  };
  private readonly handleDocumentFocus = (event: FocusEvent): void => {
    if (!this.isTourActive) {
      return;
    }

    const target = event.target as HTMLElement | null;

    if (target?.closest('.tour-tooltip') || target?.closest('.tour-backdrop')) {
      return;
    }

    event.stopPropagation();
    event.stopImmediatePropagation();

    setTimeout(() => {
      this.focusTourControls();
    });
  };

  private trapTooltipFocus(event: KeyboardEvent, tooltip: HTMLElement): void {
    const focusableElements = Array.from(
      tooltip.querySelectorAll<HTMLElement>(
        [
          'button:not([disabled])',
          'a[href]',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])',
        ].join(',')
      )
    );

    if (!focusableElements.length) {
      event.preventDefault();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;

    if (event.shiftKey && activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
      return;
    }

    if (!event.shiftKey && activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }
  private getScrollPosition(scrollParent: HTMLElement | Window): number {
    if (scrollParent === window) {
      return window.scrollY || document.documentElement.scrollTop || 0;
    }

    return (scrollParent as HTMLElement).scrollTop;
  }
  private isCurrentPositionRun(runId: number): boolean {
    return (
      runId === this.positionRunId && this.isTourActive && this.step !== null
    );
  }
  private removeActiveTarget(): void {
    document.querySelectorAll('.tour-active').forEach((element) => {
      element.classList.remove('tour-active');
    });
  }
  private focusTourControls(): void {
    if (!this.isTourActive) {
      return;
    }

    const firstButton = document.querySelector<HTMLElement>(
      '.tour-tooltip button:not([disabled])'
    );

    const backdrop = document.querySelector<HTMLElement>('.tour-backdrop');

    const elementToFocus = firstButton ?? backdrop;

    elementToFocus?.focus({
      preventScroll: true,
    });
  }
}
